import React, { useRef, useEffect, useState, useCallback } from 'react';
import { IoReloadCircle } from 'react-icons/io5';
import { TbReload } from 'react-icons/tb';
import { getLcn } from '~/utils/labeled-classnames';

// Types
interface CaptchaCanvasProps extends React.CanvasHTMLAttributes<HTMLCanvasElement> {
  className?: string;
}

interface UseCaptchaReturn {
  verified: boolean;
  Captcha: React.FC
}

const generateCaptcha = (length: number): string => {
  // Generate random string
  // Excluded confusing chars like I, O, 0, 1
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let text = '';
  for (let i = 0; i < length; i++) {
    text += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return text;
}

type CaptchaStatus = 'pending' | 'success' | 'failed'

export default function Captcha({
  length = 5,
  setCaptchaVerified,
  hideOnVerify,
}: {
  length?: number,
  setCaptchaVerified: (verified: boolean) => void
  hideOnVerify: boolean
}) {
  const [userInput, setUserInput] = useState<string>('');
  const [captchaStatus, setCaptchaStatus] = useState<CaptchaStatus>('pending')
  const [captchaText, setCaptchaText] = useState<string>(generateCaptcha(length));
  const [hide, setHide] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);


  const drawCaptcha = (text: string): void => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    canvas.width = Math.max(200, length * 35);
    canvas.height = 40;

    // Clear canvas with colorful gradient background
    const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
    gradient.addColorStop(0, '#ff9a9e');
    gradient.addColorStop(0.5, '#fecfef');
    gradient.addColorStop(1, '#fecfef');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Add colorful noise lines
    const lineColors: string[] = ['#ff6b6b', '#4ecdc4', '#45b7d1', '#96ceb4', '#ffeaa7', '#dda0dd'];
    ctx.lineWidth = 1;
    for (let i = 0; i < 8; i++) {
      ctx.strokeStyle = lineColors[Math.floor(Math.random() * lineColors.length)] + '60'; // Add transparency
      ctx.beginPath();
      ctx.moveTo(Math.random() * canvas.width, Math.random() * canvas.height);
      ctx.lineTo(Math.random() * canvas.width, Math.random() * canvas.height);
      ctx.stroke();
    }

    // Add colorful noise dots
    const dotColors: string[] = ['#ff7675', '#74b9ff', '#00b894', '#fdcb6e', '#e17055', '#a29bfe'];
    for (let i = 0; i < 50; i++) {
      ctx.fillStyle = dotColors[Math.floor(Math.random() * dotColors.length)] + '80'; // Add transparency
      ctx.beginPath();
      ctx.arc(Math.random() * canvas.width, Math.random() * canvas.height, 1, 0, 2 * Math.PI);
      ctx.fill();
    }

    // Draw each character with slight variations
    ctx.textBaseline = 'middle';
    const baseX: number = 20;
    const baseY: number = canvas.height / 2;
    const charSpacing: number = (canvas.width - 40) / text.length;

    for (let i = 0; i < text.length; i++) {
      const char: string = text[i];
      const x: number = baseX + i * charSpacing + (Math.random() - 0.5) * 8; // Random horizontal offset
      const y: number = baseY + (Math.random() - 0.5) * 12; // Random vertical offset
      const rotation: number = (Math.random() - 0.5) * 0.3; // Random rotation (-0.15 to 0.15 radians)
      const fontSize: number = 24 + Math.random() * 4; // Random font size variation

      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(rotation);

      // Vibrant rainbow colors for characters
      const colors: string[] = [
        '#e74c3c', '#f39c12', '#f1c40f', '#27ae60', '#3498db',
        '#9b59b6', '#e91e63', '#ff5722', '#795548', '#607d8b',
        '#ff6b35', '#f7931e', '#ffdc00', '#c7ea46', '#00bcd4'
      ];
      ctx.fillStyle = colors[Math.floor(Math.random() * colors.length)];

      ctx.font = `bold ${fontSize}px Arial`;
      ctx.fillText(char, 0, 0);

      ctx.restore();
    }

    // Add colorful distortion lines
    const curveColors: string[] = ['#ff6b6b', '#4ecdc4', '#45b7d1', '#96ceb4', '#ffeaa7'];
    ctx.lineWidth = 2;
    for (let i = 0; i < 3; i++) {
      ctx.strokeStyle = curveColors[Math.floor(Math.random() * curveColors.length)] + '60'; // Add transparency
      ctx.beginPath();
      ctx.moveTo(0, Math.random() * canvas.height);
      ctx.bezierCurveTo(
        canvas.width * 0.3, Math.random() * canvas.height,
        canvas.width * 0.7, Math.random() * canvas.height,
        canvas.width, Math.random() * canvas.height
      );
      ctx.stroke();
    }
  }

  const regenerate = (): void => {
    setCaptchaText(generateCaptcha(length));
  }

  const verify = (input: string): boolean => input.toUpperCase() === captchaText.toUpperCase();

  useEffect(() => {
    drawCaptcha(captchaText)
  }, [captchaText]);

  // init
  useEffect(() => {
    regenerate();
  }, []);

  useEffect(() => {
    const verified = captchaStatus === 'success'
    setCaptchaVerified(verified)

    if (verified && hideOnVerify) {
      setTimeout(() => {
        setHide(true)
      }, 1500);
    }

  }, [captchaStatus]);

  const handleVerify = (): void => {
    const isValid: boolean = verify(userInput);
    setCaptchaStatus(isValid ? 'success' : 'failed');
    if (!isValid) {
      regenerate();
      setUserInput('');
    }
  };

  const handleRefresh = (): void => {
    regenerate();
    setUserInput('');
    setCaptchaStatus('pending')
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setCaptchaStatus('pending')
    setUserInput(e.target.value);
  };

  const lcn = getLcn({
    captchaComponent: 'flex flex-col gap-2 border-2 border-gray-300 rounded p-3 bg-white',
    messageCont: [
      'text-sm p-2 rounded',
      captchaStatus === 'success' && 'bg-green-100 text-green-800',
      captchaStatus === 'failed' && 'bg-red-100 text-red-800',
    ]
  });

  const messageMap = {
    pending: '',
    success: '✓ CAPTCHA verified',
    failed: '✗ Verifiction failed',
  } as const;

  const message = messageMap[captchaStatus]

  if (hide) return ''

  return (
    <div className={lcn.captchaComponent}>
      {captchaStatus !== 'success' &&
        <>
          <div className='flex gap-4 items-end'>
            <canvas ref={canvasRef} className={`border border-gray-200 rounded-lg w-3/4`} />
            <button
              type='button'
              onClick={handleRefresh}
              className="w-1/4 gap-2 p-3 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 text-center"
            >
              <TbReload className='text-[120%] font-bold inline-block' />
            </button>
          </div>

          <div className='flex gap-4 items-end'>
            <input
              type="text"
              placeholder="Enter CAPTCHA"
              value={userInput}
              onChange={handleInputChange}
              className=" w-3/4 text-gray-800 px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-200/40"
            />
            <button
              type='button'
              onClick={handleVerify}
              disabled={!userInput.trim()}
              className="w-1/4 px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              Verify
            </button>
          </div>
        </>
      }

      {message && (
        <div className={lcn.messageCont}>
          {message}
        </div>
      )}
    </div>

  );
}